/* eslint-disable camelcase */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Components
import { LineChart } from "components/AtomsV2";

// Utils
import { createChartXAxisLabel } from "utils/charts";

function ChartLegend({ items }) {
  return (
    <div className="flex items-center gap-[14px] flex-wrap text-[12px] text-ink-2">
      {items.map((l) => (
        <span key={l.label} className="inline-flex items-center gap-[6px]">
          <i
            className="inline-block w-[9px] h-[9px] rounded-[3px]"
            style={{ background: l.color }}
          />
          {l.label}
        </span>
      ))}
    </div>
  );
}

ChartLegend.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }))
    .isRequired,
};

function EmptyChartState({ message }) {
  return (
    <div className="rec-card bg-surface-2 flex items-center justify-center text-[13px] text-ink-3 py-12 px-4">
      {message}
    </div>
  );
}

EmptyChartState.propTypes = {
  message: PropTypes.string.isRequired,
};

function hasValues(points) {
  return points.some((v) => Math.abs(v ?? 0) > 0);
}

function Charts({ data, granularity, selectedMeterName, selectedMeterIdentification }) {
  const { t } = useTranslation(["sizing"]);

  const batteryRows = useMemo(() => {
    if (!data?.length) return [];
    if (selectedMeterName === "all") {
      const aggregated = {};
      data.forEach(({ datetime_timestamp, e_bat }) => {
        if (!aggregated[datetime_timestamp]) {
          aggregated[datetime_timestamp] = { datetime_timestamp, value: 0 };
        }
        aggregated[datetime_timestamp].value += e_bat ?? 0;
      });
      return Object.values(aggregated);
    }
    return data
      .filter((d) => d.meter_id === selectedMeterName)
      .map(({ datetime_timestamp, e_bat }) => ({ datetime_timestamp, value: e_bat ?? 0 }));
  }, [data, selectedMeterName]);

  const energyBalanceRows = useMemo(() => {
    if (!data?.length || selectedMeterName === "all") return [];
    return data.map((entry) => ({
      datetime_timestamp: entry.datetime_timestamp,
      pv: entry.e_g ?? 0,
      retailer: entry.e_sup ?? 0,
      lem: entry.e_pur_pool ?? 0,
    }));
  }, [data, selectedMeterName]);

  const batteryLabels = useMemo(
    () => batteryRows.map((r) => createChartXAxisLabel(r.datetime_timestamp, granularity)),
    [batteryRows, granularity]
  );
  const batteryData = useMemo(() => batteryRows.map((r) => r.value), [batteryRows]);
  const hasBattery = useMemo(() => hasValues(batteryData), [batteryData]);

  const balanceLabels = useMemo(
    () => energyBalanceRows.map((r) => createChartXAxisLabel(r.datetime_timestamp, granularity)),
    [energyBalanceRows, granularity]
  );
  const balanceSeries = useMemo(
    () => [
      {
        name: t("results.charts.datasets.pvGeneration"),
        data: energyBalanceRows.map((r) => r.pv),
        color: "var(--warning)",
      },
      {
        name: t("results.charts.datasets.retailerEnergy"),
        data: energyBalanceRows.map((r) => r.retailer),
        color: "var(--info)",
      },
      {
        name: t("results.charts.datasets.lemEnergy"),
        data: energyBalanceRows.map((r) => r.lem),
        color: "var(--accent)",
      },
    ],
    [energyBalanceRows, t]
  );
  const hasBalance = useMemo(() => balanceSeries.some((s) => hasValues(s.data)), [balanceSeries]);

  const memberLabel =
    selectedMeterName !== "all"
      ? selectedMeterIdentification?.label || t("results.controls.notAvailable")
      : t("results.controls.community");

  return (
    <div className="flex flex-col gap-rec">
      {/* Battery storage over time */}
      <div>
        <div className="rec-section-title !mb-2 text-[15px]">
          {`${t("results.charts.batteryStorageOverTime")} (${memberLabel})`}
        </div>
        {batteryRows.length > 0 && hasBattery ? (
          <LineChart
            series={[
              {
                name:
                  selectedMeterName !== "all"
                    ? t("results.charts.batteryStorage")
                    : t("results.charts.aggregatedBatteryStorage"),
                data: batteryData,
                color: "var(--accent)",
              },
            ]}
            labels={batteryLabels}
            height={260}
            unit="kWh"
            type="area"
          />
        ) : (
          <EmptyChartState message={t("results.charts.empty_graph.battery_storage")} />
        )}
      </div>

      {/* Energy balance over time (per-member) */}
      {selectedMeterName !== "all" && energyBalanceRows.length > 0 && (
        <>
          <div className="rec-divider" />
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="rec-section-title !mb-0 text-[15px]">
                {`${t("results.charts.energyBalanceOverTime")} (${memberLabel})`}
              </div>
              <ChartLegend
                items={[
                  { label: t("results.charts.datasets.pvGeneration"), color: "var(--warning)" },
                  { label: t("results.charts.datasets.retailerEnergy"), color: "var(--info)" },
                  { label: t("results.charts.datasets.lemEnergy"), color: "var(--accent)" },
                ]}
              />
            </div>
            {hasBalance ? (
              <LineChart
                series={balanceSeries}
                labels={balanceLabels}
                height={280}
                unit="kWh"
                type="bar"
              />
            ) : (
              <EmptyChartState message={t("results.charts.empty_graph.energy_balance")} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

Charts.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      datetime_timestamp: PropTypes.string.isRequired,
      e_bat: PropTypes.number,
      e_g: PropTypes.number,
      e_sup: PropTypes.number,
      e_pur_pool: PropTypes.number,
      meter_id: PropTypes.string,
    })
  ).isRequired,
  granularity: PropTypes.string,
  selectedMeterName: PropTypes.string,
  selectedMeterIdentification: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
};

Charts.defaultProps = {
  granularity: "MIN15",
  selectedMeterName: "all",
  selectedMeterIdentification: null,
};

export default Charts;
